import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Search } from '../../../common/images/FaIcons';

import { makeGetAnnotationsForEntityTimeRange, getLoadingForEntity } from '../selectors/annotations.selectors';
import { loadAnnotations } from '../actions/annotations.actions';
import { getAnnotationColor, getAnnotationIcon } from '../utils/annotationPresentation';
import {
  getAnnotationDescriptionDisplay,
  getAnnotationSeverityLabel,
  getAnnotationTitleDisplay,
  getAnnotationTypeLabel,
} from '../utils/annotationDescriptionDisplay';
import { sortAnnotations } from '../utils/annotationSort';
import { isSupportedAnnotatableType } from '../utils/annotationTypes';
import InputField from '../../../common/components/atoms/InputField';
import LoadingSvg from '../../../common/components/atoms/Loading';
import {
  ListOuterWrapper,
  ListContainer,
  Header,
  TitleSection,
  Title,
  Count,
  Controls,
  SearchContainer,
  SortWrapper,
  SortButton,
  SortIcon,
  SortCaret,
  SortMenu,
  SortMenuItem,
  AnnotationsContainer,
  AnnotationCard,
  IconContainer,
  ContentSection,
  ExternalLinkIcon,
  TopRow,
  TypeBadge,
  SeverityBadge,
  Description,
  TimeInfo,
  EmptyState,
  LoadingContainer,
} from './AnnotationsList.styled';

function formatTime(t, annotation) {
  const startTime = annotation.start_time;
  const endTime = annotation.end_time;

  if (!startTime) return t('common.na', { defaultValue: 'N/A' });

  const start = moment(startTime);

  if (endTime && endTime !== startTime) {
    const end = moment(endTime);
    return `${start.format('MMM D, YYYY h:mm A')} - ${end.format('h:mm A')}`;
  }

  return start.format('MMM D, YYYY h:mm A');
}

function AnnotationsList(props) {
  const { t } = useTranslation();
  const {
    annotations,
    loading,
    className,
    timeRange,
    onAnnotationClick,
    entityType,
    entityId,
    annotationTypes,
    annotationsVisible,
    loadAnnotations
  } = props;

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('time');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showSortMenu, setShowSortMenu] = useState(false);

  const sortRef = useRef(null);

  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
  }, []);

  const toggleSortMenu = useCallback(() => {
    setShowSortMenu(prev => !prev);
  }, []);

  const setSort = useCallback((nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setShowSortMenu(false);
  }, []);

  const handleDocumentClick = useCallback((event) => {
    if (!showSortMenu) return;
    const el = sortRef.current;
    const hasContains = el && typeof el.contains === 'function';
    if (hasContains && !el.contains(event.target)) {
      setShowSortMenu(false);
    }
  }, [showSortMenu]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape' && showSortMenu) {
      setShowSortMenu(false);
    }
  }, [showSortMenu]);

  const handleAnnotationClick = useCallback((annotation, event) => {
    if (event.target.closest('button')) {
      return;
    }
    const { type, source_id } = annotation;
    if (type === 'task' && source_id) {
      window.open(`/tasks/${source_id}`, '_blank');
      return;
    }
    if (type === 'repair' && source_id) {
      window.open(`/repairs/${source_id}`, '_blank');
      return;
    }
    if (onAnnotationClick) {
      onAnnotationClick(annotation);
    }
  }, [onAnnotationClick]);

  // onAnnotationEdit/onAnnotationDelete can be wired by parent if needed

  const filteredAnnotations = useMemo(() => {
    const list = annotations || [];
    let filtered = list;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = list.filter((annotation) => {
        const description = (annotation.description || '').toLowerCase();
        const type = (annotation.type || '').toLowerCase();
        const typeLabel = (getAnnotationTypeLabel(t, annotation.type) || '').toLowerCase();
        const title = (getAnnotationTitleDisplay(t, annotation) || '').toLowerCase();
        const localizedDescription = (getAnnotationDescriptionDisplay(t, annotation) || '').toLowerCase();
        return description.includes(query) || localizedDescription.includes(query)
          || type.includes(query) || typeLabel.includes(query) || title.includes(query);
      });
    }
    return sortAnnotations(t, filtered, sortBy, sortOrder);
  }, [annotations, searchQuery, sortBy, sortOrder, t, timeRange && timeRange.start, timeRange && timeRange.end]);

  const getSortButtonText = useCallback(() => {
    if (sortBy === 'type') return sortOrder === 'asc' ? t('annotations.list.sort.typeAZ') : t('annotations.list.sort.typeZA');
    return sortOrder === 'desc' ? t('annotations.list.sort.timeNewest') : t('annotations.list.sort.timeOldest');
  }, [sortBy, sortOrder, t]);

  const startTs = useMemo(() => (timeRange && timeRange.start ? new Date(timeRange.start).getTime() : null), [timeRange]);
  const endTs = useMemo(() => (timeRange && timeRange.end ? new Date(timeRange.end).getTime() : null), [timeRange]);
  const typesKey = useMemo(() => JSON.stringify(annotationTypes || []), [annotationTypes]);

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, [handleDocumentClick]);

  useEffect(() => {
    if (!annotationsVisible) return;
    if (!entityType || !entityId || !startTs || !endTs) return;
    // The API only serves scopes in SUPPORTED_ANNOTATABLE_TYPES; asking for any
    // other one is a guaranteed 400 plus an error toast.
    if (!isSupportedAnnotatableType(entityType)) return;
    if (!Number.isFinite(startTs) || !Number.isFinite(endTs)) return;
    const params = {
      annotatable_type: entityType,
      annotatable_id: entityId,
      start_time: new Date(startTs).toISOString(),
      end_time: new Date(endTs).toISOString(),
      types: annotationTypes || []
    };
    loadAnnotations(params);
  }, [annotationsVisible, entityType, entityId, startTs, endTs, typesKey, loadAnnotations, annotationTypes]);

  return (
    <ListOuterWrapper>
      <ListContainer className={className}>
        <Header>
          <TitleSection>
            <Title>{t('annotations.list.title')}</Title>
            <Count>({(filteredAnnotations || []).length})</Count>
          </TitleSection>
          <Controls>
            <SearchContainer>
              <InputField
                type="text"
                prefix={<Search alignItems="center" />}
                prefixSide="left"
                compactPrefix
                onChange={e => handleSearchChange(e.target.value)}
                value={searchQuery}
                placeholder={t('annotations.list.searchPlaceholder')}
                clearable
                clearInput={() => handleSearchChange('')}
                marginBottom="0"
              />
            </SearchContainer>
            <SortWrapper ref={(el) => { sortRef.current = el; }} onKeyDown={handleKeyDown}>
              <SortButton
                onClick={toggleSortMenu}
                title={t('annotations.list.sort.openSortOptions')}
                aria-haspopup="menu"
                aria-expanded={showSortMenu}
              >
                <SortIcon>⇅</SortIcon>
                {getSortButtonText()}
                <SortCaret>▾</SortCaret>
              </SortButton>
              {showSortMenu && (
                <SortMenu role="menu">
                  <SortMenuItem onClick={() => setSort('time', 'desc')}>{t('annotations.list.sort.timeNewest')}</SortMenuItem>
                  <SortMenuItem onClick={() => setSort('time', 'asc')}>{t('annotations.list.sort.timeOldest')}</SortMenuItem>
                  <SortMenuItem onClick={() => setSort('type', 'asc')}>{t('annotations.list.sort.typeAZ')}</SortMenuItem>
                  <SortMenuItem onClick={() => setSort('type', 'desc')}>{t('annotations.list.sort.typeZA')}</SortMenuItem>
                </SortMenu>
              )}
            </SortWrapper>
          </Controls>
        </Header>

        {loading && (
          <LoadingContainer>
            <LoadingSvg />
          </LoadingContainer>
        )}
        {!loading && (filteredAnnotations || []).length === 0 && (
          <EmptyState>
            {searchQuery ? t('annotations.list.empty.noMatch') : t('annotations.list.empty.none')}
          </EmptyState>
        )}
        {!loading && (filteredAnnotations || []).length > 0 && (
          <AnnotationsContainer>
            {filteredAnnotations.map((annotation) => {
              const color = getAnnotationColor(annotation, { respectSeverity: false });
              const icon = getAnnotationIcon(annotation.type);
              const title = getAnnotationTitleDisplay(t, annotation);
              const typeLabel = getAnnotationTypeLabel(t, annotation.type) || annotation.type;
              const severityLabel = getAnnotationSeverityLabel(t, annotation.severity);
              const isClickable = (annotation.type === 'task' || annotation.type === 'repair') && annotation.source_id;
              const isInteractive = isClickable || Boolean(onAnnotationClick);

              return (
                <AnnotationCard
                  key={annotation.id}
                  onClick={e => handleAnnotationClick(annotation, e)}
                  interactive={isInteractive}
                >
                  <IconContainer color={color}>
                    {icon}
                  </IconContainer>
                  <ContentSection>
                    <TopRow>
                      <TypeBadge color={color}>{typeLabel}</TypeBadge>
                      {severityLabel && (
                        <SeverityBadge severity={annotation.severity}>
                          {severityLabel}
                        </SeverityBadge>
                      )}
                    </TopRow>
                    <Description title={title}>{title}</Description>
                    <TimeInfo>{formatTime(t, annotation)}</TimeInfo>
                  </ContentSection>
                  {isClickable && (
                    <ExternalLinkIcon title={t('annotations.list.openInNewTab')}>
                      ↗
                    </ExternalLinkIcon>
                  )}
                </AnnotationCard>
              );
            })}
          </AnnotationsContainer>
        )}
      </ListContainer>
    </ListOuterWrapper>
  );
}

AnnotationsList.propTypes = {
  annotations: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  className: PropTypes.string,
  annotationsVisible: PropTypes.bool,
  onAnnotationClick: PropTypes.func,
  entityType: PropTypes.string,
  entityId: PropTypes.number,
  timeRange: PropTypes.shape({
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date)
  }),
  annotationTypes: PropTypes.arrayOf(PropTypes.string),
  loadAnnotations: PropTypes.func.isRequired
};

AnnotationsList.defaultProps = {
  annotations: [],
  loading: false,
  className: '',
  onAnnotationClick: null,
  entityType: null,
  entityId: null,
  timeRange: null,
  annotationTypes: [],
  annotationsVisible: false
};

// Per-instance so the list gets its own memoized, entity-scoped selector: it
// reads only this tag's annotation bucket, not the shared global slice.
const makeMapStateToProps = () => {
  const getAnnotationsForRange = makeGetAnnotationsForEntityTimeRange();
  return (state, ownProps) => ({
    // Filter this entity's annotations to the chart time window and apply
    // visibility/filter rules.
    annotations: getAnnotationsForRange(state, ownProps.entityType, ownProps.entityId, ownProps.timeRange),
    loading: getLoadingForEntity(state, ownProps.entityType, ownProps.entityId),
    annotationsVisible: (state.annotations && state.annotations.visible) || false
  });
};

const mapDispatchToProps = {
  loadAnnotations
};

export default connect(makeMapStateToProps, mapDispatchToProps)(AnnotationsList);
